import React, { useState, useEffect } from "react";
import { db } from "./firebase";
import { 
  collection, 
  query, 
  where, 
  onSnapshot, 
  orderBy
} from "firebase/firestore";
import { motion, AnimatePresence } from "framer-motion";

export default function LiveSupportDashboard({ user, onBack, onAccept }) {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Listen for all chat sessions belonging to this therapist
    const q = query(
      collection(db, "chat_sessions"), 
      where("therapistId", "==", user.uid),
      orderBy("updatedAt", "desc")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const convs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setConversations(convs);
      setLoading(false);
    }, (error) => {
      console.error("Inbox listener error:", error);
      // Fallback: if index is missing, show without ordering
      const qSimple = query(
        collection(db, "chat_sessions"), 
        where("therapistId", "==", user.uid)
      );
      onSnapshot(qSimple, (snap) => {
        setConversations(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      });
    });

    return () => unsubscribe();
  }, [user.uid]);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button onClick={onBack} style={styles.backBtn}>← Back</button>
        <h1 style={styles.title}>Messages & Support</h1>
      </div>

      {loading ? (
        <div style={styles.center}>Loading conversations...</div>
      ) : conversations.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>💬</div>
          <h2 style={styles.emptyTitle}>No messages yet</h2>
          <p style={styles.emptySub}>When your clients message you from their profile, they will appear here.</p>
        </div>
      ) : (
        <div style={styles.inboxList}>
          <AnimatePresence>
            {conversations.map((conv) => (
              <motion.div 
                key={conv.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                style={styles.convCard}
                onClick={() => onAccept(conv.id)}
              >
                <div style={styles.avatar}>
                  {conv.userName?.charAt(0) || "U"}
                </div>
                <div style={styles.convInfo}>
                  <div style={styles.convTop}>
                    <h3 style={styles.userName}>{conv.userName}</h3>
                    <span style={styles.timeLabel}>
                      {conv.updatedAt ? new Date(conv.updatedAt.toMillis()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "Recently"}
                    </span>
                  </div>
                  <p style={styles.lastMsg}>
                    {conv.lastMessage || "Click to start chatting..."}
                  </p>
                </div>
                <div style={styles.arrow}>→</div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: '40px 20px',
    maxWidth: '800px',
    margin: '0 auto',
    fontFamily: "'Syne', sans-serif"
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    marginBottom: '32px'
  },
  backBtn: {
    padding: '8px 16px',
    backgroundColor: '#FFF',
    border: '1px solid #E5E7EB',
    borderRadius: '12px',
    cursor: 'pointer',
    fontWeight: '600'
  },
  title: { fontSize: '28px', color: '#003049', margin: 0 },
  inboxList: { display: 'flex', flexDirection: 'column', gap: '12px' },
  convCard: {
    display: 'flex',
    alignItems: 'center',
    padding: '20px',
    backgroundColor: '#FFF',
    borderRadius: '20px',
    boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
    border: '1px solid #F1E0D0',
    cursor: 'pointer',
    transition: 'all 0.2s',
    ':hover': { transform: 'translateX(4px)', borderColor: '#F07167' }
  },
  avatar: {
    width: '50px',
    height: '50px',
    borderRadius: '15px',
    backgroundColor: '#F07167',
    color: '#FFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    fontWeight: '700',
    marginRight: '16px'
  },
  convInfo: { flex: 1 },
  convTop: { display: 'flex', justifyContent: 'space-between', marginBottom: '4px' },
  userName: { margin: 0, fontSize: '18px', color: '#003049' },
  timeLabel: { fontSize: '12px', color: '#AAA' },
  lastMsg: { margin: 0, fontSize: '14px', color: '#666', opacity: 0.8 },
  arrow: { color: '#CCC', fontSize: '20px', marginLeft: '12px' },
  emptyState: { textAlign: 'center', padding: '80px 20px' },
  center: { textAlign: 'center', padding: '100px' }
};
